import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Row,
  Col,
  Divider,
  Avatar,
  Tag,
  Spin,
  message,
  Space,
} from "antd";
import {
  UserOutlined,
  IdcardOutlined,
  TeamOutlined,
  SaveOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import FirmaCanvas from "../firma_electronica/firma";
import { useSelector, useDispatch } from "react-redux";
import { updateUserProfile } from "../../store/auth/authSlice"; // ajusta la ruta a tu slice
import { uploadFirmaUsuario } from "../../services/users/usersServices";

const { Title, Text } = Typography;



// ─── Indicador de paso ───────────────────────────────────────────────────────
const StepBadge = ({ number, label, active }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      opacity: active ? 1 : 0.45,
      transition: "opacity 0.3s",
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: active
          ? "linear-gradient(135deg, #4f46e5, #7c3aed)"
          : "#d9d9d9",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
        transition: "background 0.3s",
      }}
    >
      {number}
    </div>
    <Text style={{ fontSize: 13, fontWeight: active ? 600 : 400 }}>
      {label}
    </Text>
  </div>
);

// ─── Componente principal ────────────────────────────────────────────────────
const PerfilTecnico = () => {
  const [form] = Form.useForm();
  const [formLoaded, setFormLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const usernameFromState = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  // Cargar datos del usuario
  useEffect(() => {
    const fetchUser = async () => {
      try {

        form.setFieldsValue({
          name: usernameFromState.name,
          rol: usernameFromState.role,
          numeroCedula: usernameFromState.numeroCedula,
          // FirmaCanvas leerá este campo desde form.getFieldValue('signature_path')
          signature_path: usernameFromState.signature_path || null,
        });

        setFormLoaded(true);
      } catch (err) {
        message.error("Error al cargar los datos del usuario.");
      } finally {
        setLoading(false);
      }
    };
    if(usernameFromState!=null){
      fetchUser();
    }

  }, [usernameFromState, form]);

  // ── Guardar cambios → multipart/form-data, mismo patrón que uploadFormatoEntrega ──
  const handleSubmit = async (values) => {
    const signatureValue = values.signature_path;

    if (!signatureValue) {
      message.warning("Debe dibujar o cargar una firma antes de guardar.");
      return;
    }

    // Si la firma no cambió (sigue siendo ruta relativa del servidor),
    // solo actualizamos los datos del perfil sin subir archivo
    const firmaEsNueva = signatureValue.startsWith("data:image");

    if (!firmaEsNueva) {
      // TODO: si quieres permitir actualizar nombre/cédula/rol sin cambiar firma,
      // llama a un endpoint PUT separado solo con JSON aquí.
      message.info("No se detectaron cambios en la firma.");
      return;
    }

    try {
      setSaving(true);
      const data = await uploadFirmaUsuario(
        usernameFromState.oid_usuario,
        values.name,
        values.rol,
        values.numeroCedula,
        signatureValue
      );

      if (!data.success) {
        throw new Error(data.message || "Error al guardar el perfil.");
      }

      // ── Actualizar Redux en caliente ───────────────────────────────────────
      // Todos los módulos que lean signature_path (ej: TablaVisitaTecnica)
      // lo reflejan sin recargar la página
      dispatch(
        updateUserProfile({
          name: values.name,
          role: values.rol,
          numeroCedula: values.numeroCedula,
          signature_path: data.data?.signature_path ?? usernameFromState.signature_path,
        })
      );

      message.success("Perfil actualizado correctamente.");
    } catch (err) {
      console.error("Error al guardar perfil:", err);
      // Mismo manejo de error que uploadFormatoEntrega
      const msgError =
        err.response?.data?.message ||
        err.message ||
        "Error al guardar los cambios.";
      message.error(msgError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: 780,
        margin: "0 auto",
        padding: "32px 16px",
      }}
    >
      {/* ── Encabezado ─────────────────────────────────────────────────── */}
      <Card
        bordered={false}
        style={{
          marginBottom: 20,
          background: "linear-gradient(120deg, #042956 0%, #042956 60%, #042956 100%)",
          borderRadius: 16,
        }}
        bodyStyle={{ padding: "24px 28px" }}
      >
        <Row align="middle" gutter={20}>
          <Col>
            <Avatar
              size={60}
              icon={<UserOutlined />}
              style={{
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
                fontSize: 28,
              }}
            />
          </Col>
          <Col flex="auto">
            <Title
              level={4}
              style={{ color: "#fff", margin: 0, fontWeight: 700 }}
            >
              {usernameFromState?.name || "Perfil del Técnico"}
            </Title>
            <Space size={8} style={{ marginTop: 6 }}>
              <Tag
                icon={<TeamOutlined />}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "2px 10px",
                }}
              >
                {usernameFromState?.role || "Sin rol"}
              </Tag>
              <Tag
                icon={<IdcardOutlined />}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "2px 10px",
                }}
              >
                CC {usernameFromState?.numeroCedula}
              </Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ── Formulario ─────────────────────────────────────────────────── */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        scrollToFirstError
      >
        {/* Sección 1 – Datos personales */}
        <Card
          bordered={false}
          style={{ borderRadius: 14, marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
          bodyStyle={{ padding: "24px 28px" }}
        >
          <StepBadge number="1" label="Datos del técnico" active />
          <Divider style={{ margin: "16px 0 20px" }} />

          <Row gutter={20}>
            {/* Nombre */}
            <Col xs={24} md={24}>
              <Form.Item
                name="name"
                label={
                  <Text strong style={{ fontSize: 13 }}>
                    Nombre completo
                  </Text>
                }
                rules={[
                  { required: true, message: "El nombre es requerido" },
                  { min: 3, message: "Mínimo 3 caracteres" },
                ]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: "#4f46e5" }} />}
                  placeholder="Nombre completo del técnico"
                  size="large"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>
            </Col>

            {/* Rol */}
                       {/* Nombre */}
            <Col xs={24} md={12}>
              <Form.Item
                name="rol"
                label={
                  <Text strong style={{ fontSize: 13 }}>
                    Rol
                  </Text>
                }
                rules={[
                  { required: true, message: "El rol es requerido" },
                ]}
              >
                <Input
                  prefix={<TeamOutlined style={{ color: "#4f46e5" }} />}
                  placeholder="Rol"
                  size="large"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>
            </Col>


            {/* Cédula */}
            <Col xs={24} md={12}>
              <Form.Item
                name="numeroCedula"
                label={
                  <Text strong style={{ fontSize: 13 }}>
                    Número de cédula
                  </Text>
                }
                rules={[
                  { required: true, message: "La cédula es requerida" },
                  {
                    pattern: /^[0-9]{6,10}$/,
                    message: "Ingrese entre 6 y 12 dígitos numéricos",
                  },
                ]}
              >
                <Input
                  prefix={<IdcardOutlined style={{ color: "#4f46e5" }} />}
                  placeholder="Ej: 1087654321"
                  size="large"
                  maxLength={12}
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Sección 2 – Firma */}
        <Card
          bordered={false}
          style={{ borderRadius: 14, marginBottom: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
          bodyStyle={{ padding: "24px 28px" }}
        >
          <StepBadge number="2" label="Firma digital" active />
          <Divider style={{ margin: "16px 0 8px" }} />

          <Text
            type="secondary"
            style={{ display: "block", marginBottom: 16, fontSize: 13 }}
          >
            Esta firma se reutilizará automáticamente en todos los formularios
            de visitas técnicas. Solo necesita registrarla una vez.
          </Text>

          {/*
            ── FirmaCanvas reutilizado ──────────────────────────────────────
            • name="signature_path"  → campo del Form donde guarda el base64
            • form={form}            → instancia del Form de Ant Design
            • formLoaded={formLoaded}→ espera a que los datos estén listos
            • disabledCambioFirma=false → el técnico puede cambiarla
          */}
          <Form.Item
            name="signature_path"
            // Sin regla "required" aquí: la firma es opcional al crear,
            // pero puedes agregar: rules={[{ required: true, message: '...' }]}
          >
            <FirmaCanvas
              name="signature_path"
              label="Firma del técnico"
              form={form}
              formLoaded={formLoaded}
              disabledCambioFirma={false}
            />
          </Form.Item>
        </Card>

        {/* ── Botón guardar ─────────────────────────────────────────────── */}
        <Row justify="end">
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={saving}
              icon={<SaveOutlined />}
              style={{
                borderRadius: 10,
                background: "linear-gradient(120deg, #52C41A, #52C41A)",
                border: "none",
                paddingInline: 36,
                height: 46,
                fontWeight: 600,
              }}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default PerfilTecnico;